import { InputToggle } from '@calefy-inc/informedMaterial';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';

// types and classes
import { Component } from './classes';
import {
  QuoteComponentProps,
  QuoteComponentValidationFunctionReturn,
} from './types';
import { ProgramBuilderQuestionInstance } from '../../FormManager/classes';
import { formatAsReadableList } from './utility';
import MultiOptionsComponent from './common/MultiOptions';

const quoteComponent = function ({
  questionInstance,
  classes = {},
  ...props
}: QuoteComponentProps) {
  const { options } = questionInstance.propsBlob;

  return (
    <InputToggle
      name={questionInstance.apiName}
      label={questionInstance.getQuoteQuestionLabel()}
      helperText={questionInstance.helpText}
      required={questionInstance.required}
      classes={classes}
      className={classes.yesNoToggle}
      options={options}
      {...props}
    />
  );
};

export function validateInputToggle(
  questionInstance: ProgramBuilderQuestionInstance,
): QuoteComponentValidationFunctionReturn {
  const errors: Array<string> = [];
  if (!questionInstance.propsBlob) {
    return [
      `Input toggle with name "${questionInstance.generateDisplayName()}" is missing some required attributes.`,
    ];
  }
  const { options } = questionInstance.propsBlob;
  if (!options || options.length === 0) {
    return [
      `Input toggle with name "${questionInstance.generateDisplayName()}" is missing some options. You must provide at least two.`,
    ];
  }
  if (options.length === 1) {
    return [
      `Input toggle with name "${questionInstance.generateDisplayName()}" is missing an option. You must provide at least two.`,
    ];
  }

  // ensure that all values are unique
  // @ts-expect-error
  const valueCountMapping = options.reduce((acc, option) => {
    const { value } = option;
    acc[value] = value in acc ? acc[value] + 1 : 1;
    return acc;
  }, {});
  const valuesWithMultiples = Object.entries(valueCountMapping).reduce(
    (acc: Array<string>, [value, count]) => {
      // @ts-expect-error
      if (count > 1) {
        acc.push(value);
      }
      return acc;
    },
    [],
  );
  if (valuesWithMultiples.length > 0) {
    errors.push(
      `Input toggle with API name ${questionInstance.apiName} has ${
        valuesWithMultiples.length == 1 ? 'a value' : 'some values'
      } which appear more than once: ${formatAsReadableList(
        valuesWithMultiples.map((value) => `"${value}"`),
      )}. Each value can appear only once.`,
    );
  }

  if (questionInstance.defaultValue) {
    const values: Array<string> = Array.from(
      // @ts-expect-error
      new Set(options.map((option) => option.value)),
    );
    if (!values.includes(questionInstance.defaultValue)) {
      errors.push(
        `Input toggle with API name ${
          questionInstance.apiName
        } has a default value ("${
          questionInstance.defaultValue
        }") which is not associated with any label. The options are ${formatAsReadableList(
          values.map((value) => `"${value}"`),
        )}.`,
      );
    }
  }

  return errors;
}

const IconComponent = () => {
  return <RadioButtonCheckedRoundedIcon fontSize='small' color='primary' />;
};

export default function createComponentRegistry() {
  return new Component({
    type: 'InputToggle',
    typeLabel: 'Input Toggle (Facultative)',
    quoteComponent,
    dataType: 'string',
    programBuilderComponent: MultiOptionsComponent,
    programBuilderValidation: validateInputToggle,
    icon: IconComponent,
  });
}

export { quoteComponent };
